/**
 * Bouton flottant chat pour le portail étudiant.
 *
 * v2 (2026-08-31) — 2 onglets :
 *   - Camarades : étudiants en ligne (endpoint /portail/me/en-ligne/)
 *   - Personnel : agents/formateurs/direction en ligne
 *                 (endpoint /messagerie-chat/contacts/ — accepte le JWT portail)
 *
 * Camarades → navigate('/portail/messagerie?tab=camarades&conv=X')
 * Personnel → navigate('/portail/messagerie?tab=personnel&conv=X')
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MessageCircle, X, Search, Loader2, MessageSquare, Users, Briefcase,
} from 'lucide-react';
import { portailApi, ChatEtudiantSummary } from '../services/portailApi';
import PhotoLightbox from '../../components/ui/PhotoLightbox';

const PUBLIC_PORTAIL_PATHS = [
  '/portail/login',
  '/portail/chercher-matricule',
  '/portail/premiere-connexion',
  '/portail/mot-de-passe-oublie',
  '/portail/maintenance',
];

// Fix 2026-09-01 : masquer le FAB sur les pages de conversation pour ne pas
// couvrir le bouton "Envoyer" du composer en bas à droite. Le FAB reste
// visible sur la liste des conversations (utile pour ouvrir un nouveau chat).
function _fabDoitEtreCache(path: string, search: string): boolean {
  if (path.startsWith('/portail/messagerie')) {
    // Cacher UNIQUEMENT quand une conversation est sélectionnée (?conv=X)
    return /(?:^|&)conv=\d+/.test(search);
  }
  return false;
}

type Tab = 'camarades' | 'personnel';

interface AgentItem {
  user_id: number;
  nom: string;
  role: string;
  role_label: string;
  photo_url: string | null;
  en_ligne: boolean;
  statut: string;
  activite: string;
}

const ROLE_LABELS: Record<string, string> = {
  agent: 'Scolarité',
  agent_caisse: 'Caisse',
  formateur: 'Formateur',
  super_admin: 'Direction',
  admin: 'Administration',
  directeur: 'Direction',
  surveillant: 'Surveillance',
};

const ROLE_COLOR: Record<string, string> = {
  agent: 'bg-emerald-600',
  agent_caisse: 'bg-yellow-600',
  formateur: 'bg-indigo-600',
  super_admin: 'bg-rose-600',
  admin: 'bg-rose-600',
  directeur: 'bg-rose-600',
  surveillant: 'bg-orange-600',
};

export const PortailChatFAB: React.FC = () => {
  const loc = useLocation();
  const path = loc.pathname;
  const isPublic = PUBLIC_PORTAIL_PATHS.some(p => path === p || path.startsWith(p + '/'))
    || path.startsWith('/portail/verifier/');
  // Fix 2026-09-01 : cacher aussi quand on est dans une conversation ouverte
  // (le FAB masquait le bouton "Envoyer" du composer).
  const fabCache = _fabDoitEtreCache(path, loc.search);
  const token = typeof window !== 'undefined'
    ? localStorage.getItem('portail_access_token') : null;

  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('camarades');

  const [items, setItems] = useState<ChatEtudiantSummary[]>([]);
  const [agents, setAgents] = useState<AgentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [opening, setOpening] = useState<string | null>(null);

  const [nbConnected, setNbConnected] = useState<number>(0);
  const [nbPersonnelConnected, setNbPersonnelConnected] = useState<number>(0);
  const [lightbox, setLightbox] = useState<
    { photo_url: string | null; nom: string; hint?: string } | null
  >(null);

  // Compteurs (étudiants + personnel) — polling 60s même drawer fermé
  useEffect(() => {
    if (!token || isPublic) return;
    let stop = false;
    const load = () => {
      portailApi.etudiantsEnLigne('tous', '')
        .then((r: any) => { if (!stop) setNbConnected(r.nb_total || 0); })
        .catch(() => { /* 401/403 silencieux */ });
      portailApi.personnelListe('tous', '')
        .then((r: any) => {
          if (!stop) {
            const nb = (r.results || []).filter((a: any) => a.en_ligne).length;
            setNbPersonnelConnected(nb);
          }
        })
        .catch(() => { /* silencieux */ });
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { stop = true; clearInterval(iv); };
  }, [token, isPublic, path]);

  // Charger la liste quand drawer ouvert ou changement onglet/recherche
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    if (tab === 'camarades') {
      portailApi.etudiantsEnLigne('tous', q.trim())
        .then((r: any) => setItems(r.results || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    } else {
      portailApi.personnelListe('tous', q.trim())
        .then((r: any) => {
          // On garde uniquement les rôles utiles pour un étudiant
          const roles = new Set(['agent', 'agent_caisse', 'formateur',
            'super_admin', 'admin', 'directeur', 'surveillant']);
          const filt = (r.results || []).filter((a: AgentItem) => roles.has(a.role));
          // Tri : en ligne d'abord, puis alpha
          filt.sort((a: AgentItem, b: AgentItem) => {
            if (a.en_ligne !== b.en_ligne) return a.en_ligne ? -1 : 1;
            return a.nom.localeCompare(b.nom, 'fr');
          });
          setAgents(filt);
        })
        .catch(() => setAgents([]))
        .finally(() => setLoading(false));
    }
  }, [open, tab, q]);

  // Grouper camarades par classe
  const groupesCamarades = useMemo(() => {
    const m = new Map<string, ChatEtudiantSummary[]>();
    items.forEach(e => {
      const k = e.classe || '—';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(e);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0], 'fr'));
  }, [items]);

  // Fix 2026-09-01 : liste plate au lieu de grouper par rôle. Ordonnée en
  // ligne d'abord (déjà trié dans useEffect), puis alpha. L'ancien grouping
  // par rôle brisait le tri en_ligne desc car chaque groupe partait à zéro
  // (un rôle avec seulement des hors-ligne remontait au-dessus d'un rôle
  // avec des connectés). Une liste plate garantit "connectés en tête".
  const personnelTrieAffich = useMemo(() => {
    const nbOnline = agents.filter(a => a.en_ligne).length;
    return { agents, nbOnline };
  }, [agents]);

  const ouvrirChatCamarade = async (etu: ChatEtudiantSummary) => {
    const id = etu.matricule_reel || etu.matricule;
    setOpening('c:' + id);
    try {
      const r = await portailApi.chatOuvrirDirecte(id);
      setOpen(false);
      navigate(`/portail/messagerie?tab=camarades&conv=${r.conversation_id}`);
    } catch (e: any) {
      alert(e?.message || 'Impossible d\'ouvrir la conversation');
    } finally {
      setOpening(null);
    }
  };

  const ouvrirChatAgent = async (agent: AgentItem) => {
    setOpening('a:' + agent.user_id);
    try {
      const r = await portailApi.personnelOuvrirDirect(agent.user_id);
      setOpen(false);
      navigate(`/portail/messagerie?tab=personnel&conv=${r.id}`);
    } catch (e: any) {
      alert(e?.message || 'Impossible d\'ouvrir la conversation');
    } finally {
      setOpening(null);
    }
  };

  if (!token || isPublic || fabCache) return null;

  const nbTotal = nbConnected + nbPersonnelConnected;

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Chat instantané"
        title={`${nbConnected} étudiant(s) · ${nbPersonnelConnected} personnel en ligne`}
        className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-105 ring-4 ring-blue-500/20 bg-blue-600 hover:bg-blue-500 text-white"
      >
        <MessageCircle size={26} />
        {nbTotal > 0 && (
          <>
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-green-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-gray-900 shadow">
              {nbTotal > 99 ? '99+' : nbTotal}
            </span>
            <span className="absolute inset-0 rounded-full animate-ping bg-blue-400/40 pointer-events-none" />
          </>
        )}
      </button>

      {/* Drawer */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full sm:w-96 max-w-[90vw] h-full bg-gray-900 text-gray-100 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-2">
              <MessageSquare size={18} className="text-blue-400" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">Chat instantané</div>
                <div className="text-[11px] text-gray-500">
                  {nbConnected} étudiant(s) · {nbPersonnelConnected} personnel
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded hover:bg-gray-800"
                aria-label="Fermer"
              ><X size={18} /></button>
            </div>

            {/* Onglets Camarades / Personnel */}
            <div className="px-3 pt-2">
              <div className="bg-gray-800/60 border border-gray-800 rounded-lg p-1 grid grid-cols-2 gap-1">
                <button
                  onClick={() => { setTab('camarades'); setQ(''); }}
                  className={`py-1.5 text-xs font-medium rounded inline-flex items-center justify-center gap-1.5 transition-colors ${
                    tab === 'camarades'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <Users size={12} /> Camarades
                  {nbConnected > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300">
                      {nbConnected}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => { setTab('personnel'); setQ(''); }}
                  className={`py-1.5 text-xs font-medium rounded inline-flex items-center justify-center gap-1.5 transition-colors ${
                    tab === 'personnel'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <Briefcase size={12} /> Personnel
                  {nbPersonnelConnected > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300">
                      {nbPersonnelConnected}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Barre recherche */}
            <div className="px-3 py-2 border-b border-gray-800">
              <div className="relative">
                <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-2 text-gray-500" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={tab === 'camarades'
                    ? 'Chercher un camarade…'
                    : 'Chercher un agent, formateur…'}
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Contenu */}
            <div className="flex-1 overflow-y-auto p-2 space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
                  <Loader2 size={16} className="animate-spin mr-2" /> Chargement…
                </div>
              ) : tab === 'camarades' ? (
                // ═══ CAMARADES ═══
                items.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 text-sm px-4">
                    {q.trim()
                      ? 'Aucun résultat.'
                      : 'Aucun étudiant en ligne pour le moment.'}
                    <div className="text-[11px] text-gray-600 mt-1">
                      Revenez dans quelques minutes.
                    </div>
                  </div>
                ) : (
                  groupesCamarades.map(([classe, etus]) => (
                    <div key={classe} className="space-y-1">
                      <div className="text-[10px] uppercase tracking-wide text-gray-500 px-2 font-semibold">
                        {classe} · {etus.length}
                      </div>
                      {etus.map(e => {
                        const id = e.matricule_reel || e.matricule;
                        const isOpening = opening === 'c:' + id;
                        return (
                          <button
                            key={id}
                            onClick={() => ouvrirChatCamarade(e)}
                            disabled={isOpening}
                            className="w-full flex items-center gap-3 p-2 rounded-lg bg-gray-800/50 hover:bg-gray-800 border border-gray-800 hover:border-blue-700 text-left disabled:opacity-60"
                          >
                            <div
                              className="relative flex-shrink-0"
                              onClick={(ev) => {
                                if (e.photo_url) {
                                  ev.stopPropagation();
                                  setLightbox({
                                    photo_url: e.photo_url,
                                    nom: `${e.prenoms || ''} ${e.nom || ''}`.trim(),
                                    hint: e.classe,
                                  });
                                }
                              }}
                            >
                              {e.photo_url ? (
                                <img src={e.photo_url} alt=""
                                  className="w-10 h-10 rounded-full object-cover cursor-zoom-in hover:ring-2 hover:ring-blue-400 transition" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-xs font-bold">
                                  {(e.prenoms?.[0] || '') + (e.nom?.[0] || '')}
                                </div>
                              )}
                              <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2 border-gray-900 pointer-events-none" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-100 truncate">
                                {e.prenoms} {e.nom}
                              </div>
                              <div className="text-[11px] text-gray-500 truncate">
                                {e.matricule_masque ? '' : `${e.matricule}${e.classe ? ' · ' : ''}`}{e.classe}
                              </div>
                            </div>
                            {isOpening
                              ? <Loader2 size={14} className="text-blue-400 animate-spin" />
                              : <MessageCircle size={14} className="text-blue-400" />}
                          </button>
                        );
                      })}
                    </div>
                  ))
                )
              ) : (
                // ═══ PERSONNEL (liste plate — connectés en tête, fix 2026-09-01) ═══
                personnelTrieAffich.agents.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 text-sm px-4">
                    {q.trim()
                      ? 'Aucun résultat.'
                      : 'Aucun personnel actuellement disponible.'}
                    <div className="text-[11px] text-gray-600 mt-1">
                      Vous pouvez toujours envoyer un message — il verra à sa reconnexion.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {personnelTrieAffich.nbOnline > 0 && (
                      <div className="text-[10px] uppercase tracking-wide text-green-400 px-2 font-semibold flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        En ligne ({personnelTrieAffich.nbOnline})
                      </div>
                    )}
                    {personnelTrieAffich.agents.map((a, idx) => {
                      const isOpening = opening === 'a:' + a.user_id;
                      const color = ROLE_COLOR[a.role] || 'bg-gray-600';
                      // Séparateur visuel entre "en ligne" et "hors ligne"
                      const isFirstOffline = !a.en_ligne
                        && idx > 0
                        && personnelTrieAffich.agents[idx - 1].en_ligne;
                      return (
                        <React.Fragment key={a.user_id}>
                          {isFirstOffline && (
                            <div className="text-[10px] uppercase tracking-wide text-gray-500 px-2 font-semibold mt-2 flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                              Hors ligne
                            </div>
                          )}
                          <button
                            onClick={() => ouvrirChatAgent(a)}
                            disabled={isOpening}
                            className={`w-full flex items-center gap-3 p-2 rounded-lg border text-left disabled:opacity-60 ${
                              a.en_ligne
                                ? 'bg-gray-800/70 hover:bg-gray-800 border-gray-800 hover:border-blue-700'
                                : 'bg-gray-900/40 hover:bg-gray-800/60 border-gray-800/60 hover:border-gray-700 opacity-80'
                            }`}
                          >
                            <div
                              className="relative flex-shrink-0"
                              onClick={(ev) => {
                                if (a.photo_url) {
                                  ev.stopPropagation();
                                  setLightbox({
                                    photo_url: a.photo_url,
                                    nom: a.nom,
                                    hint: a.role_label,
                                  });
                                }
                              }}
                            >
                              {a.photo_url ? (
                                <img src={a.photo_url} alt=""
                                  className="w-10 h-10 rounded-full object-cover cursor-zoom-in hover:ring-2 hover:ring-blue-400 transition" />
                              ) : (
                                <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center text-xs font-bold text-white`}>
                                  {(a.nom || '?').split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase()}
                                </div>
                              )}
                              <span
                                className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-gray-900 pointer-events-none ${
                                  a.en_ligne ? 'bg-green-500' : 'bg-gray-500'
                                }`}
                                title={a.en_ligne ? 'En ligne' : 'Hors ligne'}
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-100 truncate">
                                {a.nom}
                              </div>
                              <div className="text-[11px] text-gray-500 truncate">
                                {a.role_label}
                                {a.en_ligne && a.activite ? ` · ${a.activite}` : ''}
                              </div>
                            </div>
                            {isOpening
                              ? <Loader2 size={14} className="text-blue-400 animate-spin" />
                              : <MessageCircle size={14} className="text-blue-400" />}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
      {/* Lightbox photo cliquable */}
      <PhotoLightbox
        url={lightbox?.photo_url || null}
        label={lightbox?.nom}
        hint={lightbox?.hint}
        onClose={() => setLightbox(null)}
      />
    </>
  );
};

export default PortailChatFAB;
